/**
 * Which sellers have money in the pipeline — the question this page could not
 * answer before.
 *
 * The old screen required an admin to paste a vendor's cuid before it showed
 * anything at all, so "who am I about to pay?" had no answer unless you
 * already knew. Everything here comes from one server-paginated request; the
 * browser never holds more than a page of sellers, and never asks per seller.
 *
 * Two columns are deliberately kept apart. **Settlement status** is the stage
 * of the seller's money, aggregated from their settlement rows. **Latest
 * payout** is their most recent payout request. `Settlement` and
 * `PayoutRequest` share no foreign key, so one is never presented as the
 * destination of the other, and the two amounts are never added or compared.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Search, Snowflake, X } from 'lucide-react';
import { useState } from 'react';

import type { settlements } from '@terra/contracts';
import {
  EmptyState,
  Input,
  Select,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableShell,
} from '@terra/ui';

import { api } from '../../lib/api';
import { SETTLEMENT_STATUS_LABEL, formatDate, formatPaise } from '../../lib/money';

import { StagePill } from './StagePill';
import { PAYOUT_TONE, SETTLEMENT_TONE } from './tones';

export type SellerPayoutRow = settlements.SellerPayoutRow;

/**
 * `ALL` is a sentinel, not a stage.
 *
 * Radix's `Select.Item` rejects an empty string outright — it reserves `''`
 * for "no selection", so an option carrying it throws and takes the page down
 * with it. The filter therefore carries a named value and is translated back
 * to "no filter" at the query.
 */
const ALL_STAGES = 'ALL';

const STATUS_OPTIONS = [
  { value: ALL_STAGES, label: 'All stages' },
  { value: 'PENDING_COD_REMITTANCE', label: 'Awaiting COD remittance' },
  { value: 'PENDING_REFUND_WINDOW', label: 'In return window' },
  { value: 'ELIGIBLE', label: 'Ready to settle' },
  { value: 'SETTLED', label: 'Settled' },
  { value: 'HELD', label: 'On hold' },
  { value: 'REVERSED', label: 'Reversed' },
];

/**
 * Seller name is absent on purpose. The rows come from a grouped aggregate,
 * which cannot order by a column on the joined vendor — sorting a page by name
 * in the browser would order each page against itself and misrepresent the
 * rest of the result. Searching by name is unaffected.
 */
const SORT_OPTIONS = [
  { value: 'netPayable', label: 'Net payable' },
  { value: 'gross', label: 'Gross sales' },
  { value: 'commission', label: 'Commission' },
  { value: 'reserve', label: 'Reserve held' },
  { value: 'orders', label: 'Order count' },
  { value: 'lastSettledAt', label: 'Last settled' },
];

export const SELLER_PAYOUTS_KEY = 'admin-seller-payouts';

export function SellerPayouts({ onSelect }: { onSelect: (row: SellerPayoutRow) => void }) {
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState(ALL_STAGES);
  const [sortBy, setSortBy] = useState<settlements.SellerPayoutSort>('netPayable');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const filtered = Boolean(
    q || status !== ALL_STAGES || sortBy !== 'netPayable' || sortOrder !== 'desc',
  );

  const query = useQuery({
    queryKey: [SELLER_PAYOUTS_KEY, { page, pageSize, q, status, sortBy, sortOrder }],
    queryFn: () =>
      api.settlements.sellerPayouts({
        page,
        pageSize,
        sortBy,
        sortOrder,
        ...(q ? { q } : {}),
        ...(status !== ALL_STAGES ? { status: status as settlements.SettlementStatus } : {}),
      }),
    // Paging should not blank the table out underneath the operator.
    placeholderData: keepPreviousData,
  });

  const applySearch = (value: string) => {
    setQ(value.trim());
    setPage(1);
  };

  const clear = () => {
    setSearch('');
    setQ('');
    setStatus(ALL_STAGES);
    setSortBy('netPayable');
    setSortOrder('desc');
    setPage(1);
  };

  const rows = query.data?.items ?? [];
  const pagination = query.data?.pagination;

  return (
    <section aria-label="Seller payouts" className="space-y-4">
      <div>
        <h2 className="text-ink text-[17px] font-bold">Seller payouts</h2>
        <p className="text-ink-muted mt-0.5 text-[13px] font-medium">
          Seller-level settlement positions and their latest payout request.
        </p>
      </div>

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center">
        <form
          className="relative flex-1"
          onSubmit={(e) => {
            e.preventDefault();
            applySearch(search);
          }}
          role="search"
        >
          <Search
            className="text-ink-muted/60 pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            aria-label="Search sellers by store name, slug or e-mail"
            placeholder="Search store name, slug or e-mail…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onBlur={() => applySearch(search)}
            className="h-9 pl-9 text-[13px]"
          />
        </form>
        <Select
          aria-label="Filter by settlement stage"
          value={status}
          onValueChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
          className="h-9 w-full text-[12.5px] lg:w-[210px]"
          options={STATUS_OPTIONS}
        />
        <Select
          aria-label="Sort by"
          value={sortBy}
          onValueChange={(v) => {
            setSortBy(v as settlements.SellerPayoutSort);
            setPage(1);
          }}
          className="h-9 w-full text-[12.5px] lg:w-[170px]"
          options={SORT_OPTIONS}
        />
        <button
          type="button"
          onClick={() => {
            setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
            setPage(1);
          }}
          className="border-border/60 bg-surface text-ink hover:bg-surface-subtle h-9 rounded-lg border px-3 text-[12.5px] font-bold transition-colors"
          aria-label={`Sort ${sortOrder === 'desc' ? 'descending' : 'ascending'}; activate to reverse`}
        >
          {sortOrder === 'desc' ? 'High → low' : 'Low → high'}
        </button>
        {filtered && (
          <button
            type="button"
            onClick={clear}
            className="text-ink-muted hover:text-ink flex h-9 items-center gap-1 px-2 text-[12.5px] font-bold transition-colors"
          >
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        )}
      </div>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      {query.isPending ? (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-14 rounded-xl" />
          ))}
        </div>
      ) : query.isError ? (
        <div className="border-border/60 bg-surface rounded-2xl border p-8 text-center shadow-sm">
          <p className="text-ink text-[14px] font-bold">Unable to load seller payouts</p>
          <p className="text-ink-muted mx-auto mt-1 max-w-md text-[13px] font-medium">
            The seller list did not load. The money position and settlement runs above are
            unaffected.
          </p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="bg-ink hover:bg-ink/90 mt-4 h-9 rounded-lg px-4 text-[12.5px] font-bold text-white"
          >
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={filtered ? 'No sellers match those filters' : 'No seller payouts yet'}
          description={
            filtered
              ? 'Try a different search term or stage.'
              : 'Sellers appear here once an order of theirs is delivered and a settlement row is created for it.'
          }
          {...(filtered
            ? {
                action: (
                  <button
                    type="button"
                    onClick={clear}
                    className="bg-ink hover:bg-ink/90 h-9 rounded-lg px-4 text-[12.5px] font-bold text-white"
                  >
                    Clear filters
                  </button>
                ),
              }
            : {})}
        />
      ) : (
        <TableShell>
          <Table>
            <THead className="sticky top-0 z-10">
              <TR>
                <TH>Seller</TH>
                <TH className="text-right">Orders</TH>
                <TH className="text-right">Gross</TH>
                <TH className="text-right">Fees</TH>
                <TH className="text-right">Reserve</TH>
                <TH className="text-right">Net payable</TH>
                <TH>Last settled</TH>
                <TH>Settlement status</TH>
                <TH>Latest payout</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((row) => {
                // Commission plus both statutory deductions. Shown as one
                // "Fees" figure for scanning; the drawer keeps TCS and TDS
                // separate, because they are different taxes with different
                // filings and must never be reported as one number.
                const fees =
                  row.commissionPaise + row.tcsPaise + row.tdsPaise + (row.shippingFeePaise ?? 0);
                return (
                  <TR
                    key={row.vendorId}
                    className="hover:bg-surface-subtle/60 cursor-pointer transition-colors"
                    onClick={() => onSelect(row)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect(row);
                      }
                    }}
                    aria-label={`${row.storeName}, net payable ${formatPaise(row.netPayablePaise)}`}
                  >
                    <TD>
                      <div className="flex items-center gap-2">
                        <span className="text-ink max-w-[220px] truncate font-semibold">
                          {row.storeName}
                        </span>
                        {row.payoutFrozen && (
                          <span
                            className="inline-flex items-center gap-1 rounded-md bg-red-50 px-1.5 py-0.5 text-[10.5px] font-bold text-red-700"
                            title="Payouts are frozen for this seller"
                          >
                            <Snowflake className="h-3 w-3" aria-hidden /> Frozen
                          </span>
                        )}
                      </div>
                      {row.email && (
                        <span className="text-ink-muted block max-w-[220px] truncate text-[11.5px]">
                          {row.email}
                        </span>
                      )}
                    </TD>
                    <TD className="text-right tabular-nums">{row.ordersCount}</TD>
                    <TD className="text-right tabular-nums">{formatPaise(row.grossPaise)}</TD>
                    <TD className="text-ink-muted text-right tabular-nums">−{formatPaise(fees)}</TD>
                    <TD className="text-ink-muted text-right tabular-nums">
                      −{formatPaise(row.reserveHeldPaise)}
                    </TD>
                    <TD className="text-ink text-right font-bold tabular-nums">
                      {formatPaise(row.netPayablePaise)}
                    </TD>
                    <TD className="text-ink-muted whitespace-nowrap">
                      {formatDate(row.lastSettledAt)}
                    </TD>
                    <TD>
                      <StagePill tone={SETTLEMENT_TONE[row.status] ?? 'slate'}>
                        {SETTLEMENT_STATUS_LABEL[row.status] ?? row.status}
                      </StagePill>
                    </TD>
                    <TD>
                      {row.latestPayout ? (
                        <div className="flex flex-col gap-0.5">
                          <StagePill tone={PAYOUT_TONE[row.latestPayout.status] ?? 'slate'}>
                            {row.latestPayout.status}
                          </StagePill>
                          <span className="text-ink-muted text-[11px] tabular-nums">
                            {formatPaise(row.latestPayout.amountPaise)} ·{' '}
                            {formatDate(
                              row.latestPayout.releasedAt ??
                                row.latestPayout.approvedAt ??
                                row.latestPayout.requestedAt,
                            )}
                          </span>
                        </div>
                      ) : (
                        <span className="text-ink-muted text-[12px]">No request yet</span>
                      )}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </TableShell>
      )}

      {/* ── Pagination ──────────────────────────────────────────────────── */}
      {pagination && pagination.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-ink-muted text-[12.5px] font-medium">
            Showing {(pagination.page - 1) * pagination.pageSize + 1}–
            {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{' '}
            {pagination.total} {pagination.total === 1 ? 'seller' : 'sellers'}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1 || query.isFetching}
              className="border-border/60 bg-surface text-ink hover:bg-surface-subtle flex h-9 items-center gap-1 rounded-lg border px-3 text-[12.5px] font-bold transition-colors disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Previous
            </button>
            <span className="text-ink-muted px-1 text-[12.5px] font-semibold tabular-nums">
              {pagination.page} / {Math.max(1, pagination.totalPages)}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={pagination.page >= pagination.totalPages || query.isFetching}
              className="border-border/60 bg-surface text-ink hover:bg-surface-subtle flex h-9 items-center gap-1 rounded-lg border px-3 text-[12.5px] font-bold transition-colors disabled:opacity-40"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
